<template>
  <v-app>
    <v-app-bar
      fixed
      ref="appBar"
      :height="headerHeight"
      app
      elevation="0"
      class="app-header"
    >
      <!-- Column: the stridy.com site-nav row (desktop only) above the
           dashboard's own header row. Both live inside the app bar, so the
           hide-on-scroll transform slides them away together. -->
      <div class="header-stack">
        <site-header class="site-header-row" />
        <div class="header-inner" ref="headerInner">
          <div class="title-text" ref="titleBox">
            <span class="title-inner" ref="titleInner">
              Community Impact Dashboard
            </span>
            <v-btn
              icon
              class="theme-toggle"
              :aria-label="
                isDark ? 'Switch to light mode' : 'Switch to dark mode'
              "
              @click="toggleTheme"
            >
              <v-icon>{{ isDark ? "brightness_2" : "wb_sunny" }}</v-icon>
            </v-btn>
            <!-- Below 800px the site-nav row is hidden and its links collapse
                 into this burger, sitting beside the theme toggle. -->
            <v-menu bottom left offset-y content-class="site-menu">
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  class="site-menu-toggle"
                  aria-label="Stridy site menu"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>menu</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <template v-for="item in siteNav">
                  <v-list-item
                    v-if="!item.children"
                    :key="item.label"
                    :href="item.href"
                    target="_top"
                    :class="{ 'site-menu__current': item.current }"
                  >
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item>
                  <template v-else>
                    <v-subheader :key="item.label">{{
                      item.label
                    }}</v-subheader>
                    <v-list-item
                      v-for="child in item.children"
                      :key="child.label"
                      :href="child.href"
                      target="_top"
                      class="site-menu__child"
                    >
                      <v-list-item-title>{{ child.label }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </template>
                <v-divider />
                <v-list-item :href="accountUrl" target="_top">
                  <v-list-item-title>Account</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <!-- On desktop this wrapper is `display: contents`, so the search and
               filters are direct flex children of .header-inner (the three-zone
               row). Below 800px it becomes a real, non-wrapping flex row that holds
               both on one line beneath the title. -->
          <div class="header-controls">
            <div class="header-search">
              <team-filter />
            </div>
            <div class="header-filters">
              <filter-bar />
            </div>
          </div>
        </div>
      </div>
    </v-app-bar>

    <v-main>
      <router-view />
      <!-- The stridy.com footer closes the page, scrolling with the content
           below the dashboard. -->
      <site-footer />
    </v-main>

    <!-- Single app-wide snackbar; any component raises it via the store's
         showSnackbar action (see src/store/actions.js). -->
    <v-snackbar
      v-model="snackbar"
      :timeout="snackbarState.timeout"
      :color="snackbarState.color"
      bottom
      class="app-snackbar"
    >
      {{ snackbarState.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import FilterBar from "./components/FilterBar.vue";
import SiteFooter from "./components/SiteFooter.vue";
import SiteHeader from "./components/SiteHeader.vue";
import TeamFilter from "./components/TeamFilter.vue";
import { TABLET_WIDTH } from "./constants/breakpoints";
import {
  SITE_ACCOUNT_URL,
  SITE_HEADER_PX,
  SITE_NAV,
} from "./constants/siteNav";
import { storeThemeIsDark } from "./utils/theme";

const TITLE_MAX_PX = 26;
const TITLE_MIN_PX = 20;
// Space (px) the theme toggle + its left margin reserve inside the title box.
// naturalWidth measures only the title TEXT, so this is added back wherever the
// fit logic sizes the box, otherwise the text is fitted to the whole box (text +
// button) and truncates by the button's width. Tune this if the toggle changes
// size — bump it up if the title still clips, down if there's a gap before the
// button.
const TITLE_TOGGLE_PX = 60;
// Same reserve for the site-menu burger, which shares the title row below the
// wrap breakpoint (above it the burger is hidden and the site-nav row carries
// the links instead).
const TITLE_BURGER_PX = 44;
// Height of the dashboard's own header row at/above the wrap breakpoint.
const HEADER_BASE_PX = 88;
// Total app-bar height at/above the wrap breakpoint: the site-nav row stacked
// on the dashboard row. Below it the site-nav row is hidden (its links move
// into the burger menu), so only the measured dashboard row counts.
const DESKTOP_HEADER_PX = HEADER_BASE_PX + SITE_HEADER_PX;
// At/above this width the header title and filters share one row; below it the
// filters drop into a full-width strip. Matches $tablet-width in mixins.scss.
const WRAP_BREAKPOINT = TABLET_WIDTH;

export default {
  name: "App",

  components: { FilterBar, SiteFooter, SiteHeader, TeamFilter },

  data: () => ({
    siteNav: SITE_NAV,
    accountUrl: SITE_ACCOUNT_URL,
    naturalWidth: 0,
    isFitting: false,
    headerHeight:
      window.innerWidth >= WRAP_BREAKPOINT ? DESKTOP_HEADER_PX : HEADER_BASE_PX,
    scrollOffset: 0, // how far (px) the header is currently slid up
  }),

  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    snackbarState() {
      return this.$store.state.snackbar;
    },
    // v-model proxy so dismissing (Close / timeout) writes back to the store.
    snackbar: {
      get() {
        return this.$store.state.snackbar.show;
      },
      set(show) {
        this.$store.commit("changeSnackbar", { show });
      },
    },
  },

  mounted() {
    this.onResize = () => this.update();
    window.addEventListener("resize", this.onResize);

    this.onScroll = () => this.handleScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    // A reload can restore a mid-page scroll position, so set the initial slide
    // from where we actually are rather than assuming the top.
    this.handleScroll();

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.$refs.titleBox);
      this.ro.observe(this.$refs.headerInner);
    }

    const run = () => {
      this.measureNatural();
      this.update();
    };
    // Derive the floor immediately (with whatever font is loaded so far)...
    this.$nextTick(run);
    // ...then re-measure once the web font is ready, at the real glyph widths.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run);
    }
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("scroll", this.onScroll);
    if (this.ro) this.ro.disconnect();
  },

  methods: {
    update() {
      this.fit();
      this.measureHeader();
      // The bar may have grown/shrunk (e.g. filters wrapping) or crossed the
      // breakpoint: re-clamp the slid offset to what's still hideable.
      if (this.scrollOffset > this.maxHeaderOffset()) {
        this.setHeaderOffset(this.maxHeaderOffset());
      }
    },
    // How far (px) the app bar may slide up: exactly the site-nav row's height.
    // That row scrolls away; the dashboard row below it comes to rest against
    // the top of the viewport and stays there. Below the wrap breakpoint the
    // site row isn't rendered, so nothing hides and the bar is fully anchored.
    maxHeaderOffset() {
      return window.innerWidth >= WRAP_BREAKPOINT ? SITE_HEADER_PX : 0;
    },
    // Tie the slide to the absolute scroll POSITION, not the scroll delta: the
    // bar is pushed up by however far the page is scrolled, capped at the site
    // row's height. So the site row peels away over the first SITE_HEADER_PX of scrolling
    // and stays gone for the rest of the page — it only comes back as the page
    // returns to the top (fully shown at y = 0), instead of a delta-driven bar
    // that reappears on any upward flick mid-page.
    handleScroll() {
      // Clamp to >= 0 so iOS/trackpad overscroll bounce (negative scrollY)
      // doesn't push the header the wrong way.
      const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const next = Math.min(this.maxHeaderOffset(), y);
      if (next !== this.scrollOffset) this.setHeaderOffset(next);
    },
    setHeaderOffset(px) {
      this.scrollOffset = px;
      const el = this.$refs.appBar && this.$refs.appBar.$el;
      if (el) el.style.transform = px ? `translateY(${-px}px)` : "";
    },
    // Vuetify toolbars don't auto-grow, and the `app` layout offsets <v-main>
    // from the height PROP (not the rendered content). So when the filters wrap
    // below the title we measure the real content height and feed it back to the
    // prop — keeping the bar tall enough and <v-main> pushed down correctly.
    measureHeader() {
      if (window.innerWidth >= WRAP_BREAKPOINT) {
        this.headerHeight = DESKTOP_HEADER_PX;
        return;
      }
      const el = this.$refs.headerInner;
      if (el) this.headerHeight = el.offsetHeight;
    },
    measureNatural() {
      const inner = this.$refs.titleInner;
      if (!inner) return;
      const prev = inner.style.fontSize;
      inner.style.fontSize = TITLE_MAX_PX + "px";
      // scrollWidth ignores the clamped/overflowing box width of the parent.
      this.naturalWidth = inner.scrollWidth + 5;
      inner.style.fontSize = prev;
    },
    fit() {
      if (this.isFitting) return;
      this.isFitting = true;
      window.requestAnimationFrame(() => {
        this.isFitting = false;
        const box = this.$refs.titleBox;
        const inner = this.$refs.titleInner;
        if (!box || !inner || !this.naturalWidth) return;

        // Inline (>=800px): lock the box to the text-at-min-font width (plus the
        // toggle's reserved width) so the filters shrink instead of the text
        // clipping. Strip (<800px): the title has its own full-width row, so no
        // lock is needed.
        // Buttons sharing the title row: the theme toggle always, plus the site
        // burger below the wrap breakpoint.
        const isDesktop = window.innerWidth >= WRAP_BREAKPOINT;
        const buttonsPx = isDesktop
          ? TITLE_TOGGLE_PX
          : TITLE_TOGGLE_PX + TITLE_BURGER_PX;

        if (isDesktop) {
          const floorWidth =
            Math.ceil((this.naturalWidth * TITLE_MIN_PX) / TITLE_MAX_PX) +
            buttonsPx;
          box.style.minWidth = floorWidth + "px";
        } else {
          box.style.minWidth = "";
        }

        // Fit the font to the width left for the TEXT (box minus the toggle) at
        // every breakpoint: it stays at the max wherever the text fits (incl. the
        // 450-800px strip) and shrinks toward the min when that space is narrower
        // than the text (incl. <450px mobile).
        //
        // Measure the box at the MAX font first: the title box is content-sized
        // (flex 0 0 auto, shrinking only in the dense band), so reading its width
        // at the current font would let a smaller font shrink the box, which
        // shrinks the font further — a ratchet that sticks the title at the min.
        // At the max font clientWidth reflects the real allotted width instead.
        inner.style.fontSize = TITLE_MAX_PX + "px";
        const avail = Math.max(0, box.clientWidth - buttonsPx);
        const px = Math.min(
          TITLE_MAX_PX,
          Math.max(TITLE_MIN_PX, (TITLE_MAX_PX * avail) / this.naturalWidth)
        );
        inner.style.fontSize = px + "px";
      });
    },
    toggleTheme() {
      const next = !this.$vuetify.theme.dark;
      this.$vuetify.theme.dark = next;
      storeThemeIsDark(next);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/mixins.scss";

// Strip layout (<800px): the search shrinks on its own down to this width, after
// which all three shrink together. It's the search's flex-basis (the pivot), not
// a hard floor — below it the search keeps shrinking alongside the filters. Tune
// to taste.
$header-search-pivot: 160px;

// At/above this width the search locks to the TRUE viewport centre (the title and
// filter zones become equal-width flex flanks), so hiding the Country dropdown
// (team selected) no longer shifts it. Below it, the search stays centred between
// the zones (and drifts a little when Country hides — the accepted trade-off).
// Raise this if the fixed-width centred search starts crowding/truncating the
// flanks; lower it to viewport-centre at narrower widths.
$header-center-min: 1000px;

.app-header {
  // Panel colour — the same container surface as the dashboard cards below it,
  // so the header reads as chrome of the same page rather than a separate slab;
  // the drop-shadow is what lifts it. Flips with the theme. !important overrides
  // Vuetify's default sheet background.
  background-color: var(--v-panel-base) !important;
  // We drive the hide-on-scroll transform manually (1:1 with the scroll delta),
  // so disable Vuetify's built-in transition or the motion would lag.
  transition: none !important;
  filter: drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.2));

  ::v-deep .v-toolbar__content {
    padding: 0;
  }
}

// Stacks the site-nav row above the dashboard header row inside the app bar.
.header-stack {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

// The site-nav row is desktop-only: below the wrap breakpoint its links live in
// the burger menu next to the theme toggle instead.
.site-header-row {
  @include below-desktop {
    display: none;
  }
}

// ...and the burger is the inverse — only below that breakpoint, where the auto
// left margin pushes it to the far right of the title row: [title + theme
// toggle] ......... [burger].
.site-menu-toggle {
  flex: 0 0 auto;
  margin-left: auto;

  @media (min-width: #{$tablet-width}) {
    display: none;
  }
}

// A light drop-shadow is invisible on the dark page, so deepen it to keep the
// header separated from the content scrolling beneath.
.theme--dark .app-header {
  filter: drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.6));
}

.theme-toggle {
  flex: 0 0 auto;
  margin-left: 4px;
}

// At/above 800px the header is a three-zone bar (YouTube-style): the title is
// flush-left, the team/superteam search sits centred, and the remaining filters
// are flush-right. The search carries `margin: 0 auto`, so as the window narrows
// the free space on either side of it (the gaps between the three zones) is
// consumed FIRST; the fixed `column-gap` is the minimum gutter that survives.
// Only once that free space is gone does the deficit fall on the search, which
// is the sole shrinkable item (title + filters hold their width) — so it narrows
// instead of overlapping its neighbours.
// Below 800px it falls back to a strip layout: the title takes the top row, then
// the search + filters share the row beneath it (as they did before the revamp).
// Only on mobile (<450px) does each of those drop to its own full-width row.
.header-inner {
  display: flex;
  align-items: center;
  column-gap: 24px;
  width: 100%;
  // Desktop: fill whatever the app bar has left under the site-nav row.
  flex: 1 1 auto;
  padding: 0 25px;

  @include below-desktop {
    flex: 0 0 auto;
    flex-wrap: wrap;
    column-gap: 12px;
    row-gap: 10px;
    padding: 10px 25px;
  }

  @include mobile {
    padding: 10px 16px;
  }
}

// Groups the search + filters. Desktop: `display: contents` so both become
// direct children of the three-zone .header-inner flex. Below 800px: a real
// non-wrapping flex row that sits full-width beneath the title, so the two never
// snap onto separate lines while shrinking. Mobile: allow them to stack.
.header-controls {
  display: contents;

  @include below-desktop {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    flex: 1 1 100%;
    column-gap: 12px;
    order: 1;
  }

  @include mobile {
    flex-wrap: wrap;
  }
}

// Centre zone: preferred width up to 440px, shrinkable to 0; the auto side
// margins keep it centred and collapse before it starts to shrink.
.header-search {
  flex: 0 1 clamp(240px, 32vw, 440px);
  min-width: 0;
  margin: 0 auto;
  border-radius: 8px;
  padding-bottom: 1px;
  overflow: hidden;

  ::v-deep .team-filter,
  ::v-deep .v-input {
    width: 100%;
  }

  // >=$header-center-min: fixed width, no auto margins — the equal-width flanks
  // (see .title-text / .header-filters) centre it on the viewport, so a hidden
  // Country dropdown can't shift it.
  @media (min-width: $header-center-min) {
    flex: 0 0 clamp(240px, 32vw, 440px);
    margin: 0;
  }

  // 450..800px: grow to take the row's slack while the filters hold their
  // preferred width (flex-grow: 0), so the search shrinks FIRST, alone, down to
  // its flex-basis ($header-search-pivot). Past that point there's a real deficit
  // and both shrink in proportion to their bases — all three narrow together.
  // min-width: 0 lets the search shrink below the pivot in that phase; the nowrap
  // parent means it never wraps. Mobile: own full-width row.
  @include below-desktop {
    flex: 1 1 $header-search-pivot;
    min-width: 0;
    margin: 0;
  }

  @include mobile {
    flex: 1 1 100%;
    min-width: 0;
  }
}

// Right zone: holds its natural width (never shrinks) so the search absorbs the
// squeeze instead of the filters overlapping it — except in the dense band
// (800..1050px) where it shrinks too, letting its inner filters truncate.
.header-filters {
  flex: 0 0 auto;
  min-width: 0;

  @include header-dense {
    flex-shrink: 1;
  }

  // >=$header-center-min: equal-width flank opposite the title, so the search
  // sits at the true viewport centre. Its content stays right-aligned (FilterBar
  // uses justify-end), so a hidden Country dropdown just leaves empty space
  // inside this flank without moving the search.
  @media (min-width: $header-center-min) {
    flex: 1 1 0;
  }

  // 450..800px: hold the preferred width (grow: 0) while the search shrinks
  // first; once the search bottoms out at its min, shrink (shrink: 1) to fit.
  @include below-desktop {
    flex: 0 1 auto;
  }

  @include mobile {
    flex: 1 1 100%;
  }
}

// The title box grows to fill the space left of the filters; the empty area to
// the right of the text is the gap that collapses first (font stays at max until
// the text fills the box, then JS scales it down to the min font). JS sets an
// inline min-width (= text width at the min font, derived from naturalWidth) so
// the box locks at that floor and the filters shrink instead of the text
// clipping. min-width starts at 0 — JS sets the real floor on mount.
.title-text {
  // Hold the title's natural width on desktop so the deficit falls on the search
  // (the sole shrinkable zone), not the heading — except in the dense band
  // (800..1050px) where it shrinks too and the fit JS scales the font down.
  // Flex row so the title text ellipsizes while the theme toggle keeps its space
  // (a plain block would let the text fill the width and push the toggle past the
  // overflow:hidden edge, clipping it once the box shrinks).
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 0;
  overflow: hidden;
  text-wrap: nowrap;

  @include header-dense {
    flex-shrink: 1;
  }

  // >=$header-center-min: equal-width flank opposite the filters, so the search
  // sits at the true viewport centre. Title text stays left-aligned; the empty
  // space to its right is this flank absorbing its half of the free width.
  @media (min-width: $header-center-min) {
    flex: 1 1 0;
  }

  // Own the top row of the strip so the search + filters wrap together beneath
  // it. The row reads [title + theme toggle] ... [burger]: the burger's auto
  // left margin takes all the slack, so no justify-content is needed (and a
  // centred group would fight it).
  @include below-desktop {
    flex: 1 1 100%;
  }
}

.title-inner {
  display: inline-block;
  // Shrink (ellipsis) instead of shoving the theme toggle out of the clipped box.
  flex: 0 1 auto;
  min-width: 0;
  font-weight: bold;
  font-size: 26px;
  line-height: 1.2;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

// Softer corners on the global snackbar. Vuetify 2.6 names the visible bar
// `.v-snack__wrapper` (not `.v-snackbar__wrapper`).
.app-snackbar ::v-deep .v-snack__wrapper {
  margin-bottom: 24px;
  border-radius: 12px;
  // The status colors are light pastels, so force dark text (message + the
  // Close action) for legibility in both light and dark mode.
  color: #163223;

  .v-btn {
    color: #163223;
  }
}
</style>

<!-- Unscoped: v-menu content is teleported to the app root, outside this
     component's scope attribute. -->
<style lang="scss">
@import "@/styles/variables.scss";

.site-menu {
  // Nest the About submenu under its subheader.
  .site-menu__child .v-list-item__title {
    padding-left: 12px;
  }

  // The page you're on (Impact — this dashboard), matching the underline the
  // desktop bar gives it.
  .site-menu__current .v-list-item__title {
    font-weight: 700;
    color: $color-text-primary;
  }
}
</style>
